import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { isNil } from 'lodash';
import { CompaniesService } from 'api';
import {
  DELETE_FUND_INVESTMENT_DEFAULT_ERROR_MESSAGE,
  DELETE_FUND_INVESTMENT_DEFAULT_SUCCESS_MESSAGE,
  FUND_INVESTMENT_SERVICES_ERROR_MESSAGES,
} from 'common/constants/services/fundInvestments';
import { SuperAgentError } from 'common/types/services';
import { getNumberValue, throwError } from 'utilities';
import {
  DeleteFundInvestmentParams,
  DeleteFundInvestmentResponse,
  UseDeleteFundInvestmentReturn,
} from './useDeleteFundInvestment.types';

const deleteFundInvestment = async (
  params: DeleteFundInvestmentParams
): Promise<DeleteFundInvestmentResponse | null> => {
  const { fundInvestmentId } = params;

  if (isNil(fundInvestmentId)) {
    return throwError(DELETE_FUND_INVESTMENT_DEFAULT_ERROR_MESSAGE);
  }

  try {
    await CompaniesService.apiCompaniesDelete(fundInvestmentId);

    return {
      fundInvestmentId,
      responseMessage: DELETE_FUND_INVESTMENT_DEFAULT_SUCCESS_MESSAGE,
    } as DeleteFundInvestmentResponse;
  } catch (error) {
    const { status } = error as SuperAgentError;

    return throwError(
      FUND_INVESTMENT_SERVICES_ERROR_MESSAGES?.[getNumberValue(status)] ?? DELETE_FUND_INVESTMENT_DEFAULT_ERROR_MESSAGE,
      error
    );
  }
};

const useDeleteFundInvestment = (): UseDeleteFundInvestmentReturn => {
  const queryClient = useQueryClient();

  const deleteFundInvestmentMutation: UseMutationResult<
    DeleteFundInvestmentResponse | null, // Response type (null if error)
    Error, // Error type
    DeleteFundInvestmentParams // Parameters type
  > = useMutation({
    mutationFn: deleteFundInvestment,
  });

  const { isSuccess, isError, mutateAsync } = deleteFundInvestmentMutation;

  if (isSuccess) {
    // Invalidate queries to refetch the data after mutation success
    queryClient.invalidateQueries({ queryKey: [] });

    // Clean the mutation internal state
    deleteFundInvestmentMutation.reset();
  }

  return {
    isError,
    mutateAsync,
  };
};

export default useDeleteFundInvestment;
